import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import * as z from 'zod';

import { CadRenderer } from '#components/cad-renderer/cad-renderer.js';
import { ActiveDesignContextProvider } from '#components/cad-renderer/context/active-design-context-provider.js';
import { CadstromColorProviderContextProvider } from '#components/cad-renderer/context/cadstrom-color-provider-context-provider.js';
import { CoreModelContext } from '#components/cad-renderer/context/core-model-context.js';
import {
	InteractionManagerContext,
	useSetUpInteractionManagerContext,
} from '#components/cad-renderer/context/interaction-manager-context.js';
import { useProjectModelImageUrlResolver } from '#components/cad-renderer/use-project-model-image-url-resolver.js';
import { RegisterDesignSearchProvider } from '#components/command-bar/providers/design-search-provider/design-search-provider.js';
import { RegisterModuleSearchProvider } from '#components/command-bar/providers/module-search-provider/module-search-provider.js';
import { RegisterNetSearchProvider } from '#components/command-bar/providers/net-search-provider/net-search-provider.js';
import { Loading } from '#components/loading.js';
import { GenericErrorBoundary } from '#screens/error-boundaries/generic-error-boundary.js';
import { DESIGN_CANVAS_ROUTE, getDesignCanvasSearch } from './design-canvas-route-utils.js';

import type { ActiveDesignSelection } from '#components/cad-renderer/context/active-design-context.js';
import type { CadRendererActiveView } from '#components/cad-renderer/sync/sync-types.js';

export const Route = createFileRoute(
	'/_authorization-gate/orgs/$orgName/projects/$projectId/revisions/$revisionId/design-canvas',
)({
	validateSearch: z.object({
		designId: z.string().optional(),
		instanceId: z.string().optional(),
	}),
	staticData: {
		authorize: (ability) => ability.can('view', 'project'),
	},
	loader: async ({ context: { queryClient, getProjectPreviewOptions, getRawProjectModelOptions } }) => {
		void queryClient.ensureQueryData(getRawProjectModelOptions);
		const { projectPreview } = await queryClient.ensureQueryData(getProjectPreviewOptions);
		return { projectPreview };
	},
	head: ({ loaderData }) => ({
		meta: [
			{
				title: (loaderData ? loaderData.projectPreview.name : 'Project') + ' • Design Canvas • Cadstrom',
			},
		],
	}),
	component: DesignCanvasPage,
	pendingComponent: Loading,
});

function DesignCanvasPage() {
	const router = useRouter();
	const { orgName, projectId, revisionId } = Route.useParams();
	const { designId: requestedDesignId, instanceId: requestedInstanceId } = Route.useSearch();
	const { getRawProjectModelOptions } = Route.useRouteContext();
	const {
		data: { projectModel },
	} = useSuspenseQuery(getRawProjectModelOptions);

	const imageUrlResolver = useProjectModelImageUrlResolver(projectId, revisionId);
	const interactionManagerContextValue = useSetUpInteractionManagerContext(projectModel, {
		syncScope: { projectId, revisionId },
	});
	const coreModelContextValue = useMemo(() => ({ coreModel: projectModel }), [projectModel]);
	const firstSchematicDesignId = useMemo(
		() =>
			Object.values(projectModel.designs)
				.filter((design) => design.modelType === 'schematic')
				.toSorted((first, second) => first.sheetIndex - second.sheetIndex)[0]?.designId,
		[projectModel.designs],
	);
	const initialDesignId =
		requestedDesignId !== undefined && projectModel.designs[requestedDesignId]?.modelType === 'schematic' ?
			requestedDesignId
		:	firstSchematicDesignId;
	const initialInstanceId = initialDesignId === requestedDesignId ? requestedInstanceId : undefined;
	const initialSelection = useMemo<ActiveDesignSelection | undefined>(() => {
		if (initialDesignId === undefined) {
			return undefined;
		}

		const design = projectModel.designs[initialDesignId];
		const instanceIsValid =
			initialInstanceId !== undefined &&
			design?.modelType === 'schematic' &&
			design.multiInstanceConfiguration?.instances.some((i) => i.instanceId === initialInstanceId) === true;
		return { designId: initialDesignId, instanceId: instanceIsValid ? initialInstanceId : undefined };
	}, [initialDesignId, initialInstanceId, projectModel.designs]);

	const openDesignCanvasWindow = useCallback(
		(activeView: CadRendererActiveView) => {
			const href = router.buildLocation({
				to: DESIGN_CANVAS_ROUTE,
				params: { orgName, projectId, revisionId },
				search: getDesignCanvasSearch(activeView),
			}).href;
			window.open(href, '_blank');
		},
		[orgName, projectId, revisionId, router],
	);

	return (
		<CoreModelContext value={coreModelContextValue}>
			<CadstromColorProviderContextProvider>
				<ActiveDesignContextProvider initialSelection={initialSelection}>
					<RegisterDesignSearchProvider />
					<RegisterModuleSearchProvider />
					<RegisterNetSearchProvider />

					<InteractionManagerContext value={interactionManagerContextValue}>
						<div className='flex h-full min-h-0 w-full min-w-0 grow basis-0 p-2'>
							<GenericErrorBoundary>
								<CadRenderer
									revisionId={revisionId}
									projectModel={projectModel}
									className='h-full min-h-0 w-full min-w-0 rounded-lg border border-gray-a4 bg-blue-2'
									placeholderMessage='Select a sheet from the dropdown above'
									imageUrlResolver={imageUrlResolver}
									onOpenInNewWindow={openDesignCanvasWindow}
								/>
							</GenericErrorBoundary>
						</div>
					</InteractionManagerContext>
				</ActiveDesignContextProvider>
			</CadstromColorProviderContextProvider>
		</CoreModelContext>
	);
}
